export const MCP_STALE_MS = 12 * 60_000;
const DAY_MS = 24 * 60 * 60_000;
const statuses = new Set(['healthy', 'unhealthy', 'blocked', 'disabled', 'unknown', 'stale', 'unsupported', 'invalid', 'consent-required']);
const record = (value) => !!value && typeof value === 'object' && !Array.isArray(value);
const code = (value) => typeof value === 'string' && /^[a-z][a-z0-9_]{0,79}$/.test(value);
/** A display name as the desktop may send one: the connector-label shape (letters, digits, spaces, a little punctuation), 1 to 40 characters; anything else is dropped, not fatal. */
const displayName = (value) => typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9 .&'()+-]{0,39}$/.test(value);
const stamp = (value, now) => typeof value === 'number' && Number.isSafeInteger(value) && value > 0 && value <= now + 60_000;
export function parseMcpSnapshot(value, now = Date.now()) {
    if (!record(value) || !stamp(value.updatedAt, now) || !['ready', 'partial', 'error', 'disabled'].includes(String(value.discoveryState)) || !Array.isArray(value.servers) || value.servers.length > 64)
        return null;
    // Discovery turned off lists no server, whatever the snapshot carries (the desktop app sends none): every reader of the
    // snapshot (the Connections list, the Bluebeam row in connectorInventory, the chat) then treats it as no local servers.
    const listed = value.discoveryState === 'disabled' ? [] : value.servers;
    const names = new Set();
    if (value.windowsDiscoveryState !== undefined && !['disabled', 'unavailable', 'ready', 'error'].includes(String(value.windowsDiscoveryState)))
        return null;
    const servers = [];
    for (const entry of listed) {
        if (!record(entry) || typeof entry.name !== 'string' || !entry.name.trim() || entry.name.length > 200 || /[\u0000-\u001f\u007f]/.test(entry.name)
            || names.has(entry.name.toLowerCase()) || !['claude-desktop', 'claude-code', 'bluebeam-installed', 'windows-odr', 'conflict'].includes(String(entry.source)) || !['stdio', 'http', 'ws'].includes(String(entry.transport))
            || !statuses.has(entry.status) || !Number.isSafeInteger(entry.toolCount) || Number(entry.toolCount) < 0 || Number(entry.toolCount) > 10_000
            || !Array.isArray(entry.samples) || entry.samples.length > 1000 || (entry.errorCode != null && !code(entry.errorCode)))
            return null;
        names.add(entry.name.toLowerCase());
        const samples = [];
        const seen = new Set();
        for (const sample of entry.samples) {
            if (!record(sample) || !stamp(sample.checkedAt, now) || !['healthy', 'unhealthy'].includes(String(sample.status)) || typeof sample.durationMs !== 'number' || !Number.isFinite(sample.durationMs) || sample.durationMs < 0 || sample.durationMs > 600_000)
                return null;
            if (sample.checkedAt < now - DAY_MS || seen.has(sample.checkedAt))
                continue;
            seen.add(sample.checkedAt);
            samples.push({ checkedAt: sample.checkedAt, status: sample.status, durationMs: sample.durationMs });
        }
        samples.sort((a, b) => a.checkedAt - b.checkedAt);
        servers.push({ name: entry.name, ...(displayName(entry.displayName) ? { displayName: entry.displayName } : {}), source: entry.source, transport: entry.transport, status: entry.status,
            toolCount: Number(entry.toolCount), ...(stamp(entry.checkedAt, now) ? { checkedAt: entry.checkedAt } : {}),
            ...(typeof entry.errorCode === 'string' ? { errorCode: entry.errorCode } : {}), samples });
    }
    return { updatedAt: value.updatedAt, discoveryState: value.discoveryState, windowsDiscoveryState: value.windowsDiscoveryState, servers };
}
/**
 * The native dialog a row's button may request, or null when the row has none: a Windows connector waiting for the
 * per-session consent, or a project .mcp.json server (any source) whose configuration changed since Gyro adopted it
 * and now waits for the person to confirm the new one. The gesture only asks; the desktop app decides and grants.
 */
export function mcpConnectAction(server) {
    if (server.status !== 'consent-required')
        return null;
    if (server.errorCode === 'configuration_changed')
        return 'confirm';
    return server.source === 'windows-odr' ? 'connect' : null;
}
export function currentMcpStatus(server, now = Date.now()) {
    return (server.status === 'healthy' || server.status === 'unhealthy') && (!server.checkedAt || now - server.checkedAt > MCP_STALE_MS) ? 'stale' : server.status;
}
export function mcpAvailability(samples, now = Date.now()) {
    const start = now - DAY_MS;
    const buckets = Array.from({ length: 48 }, (_, index) => ({ start: start + index * DAY_MS / 48, end: start + (index + 1) * DAY_MS / 48, healthy: 0, unhealthy: 0 }));
    const seen = new Set();
    let total = 0;
    let healthy = 0;
    for (const sample of samples) {
        if (sample.checkedAt < start || sample.checkedAt > now || seen.has(sample.checkedAt))
            continue;
        seen.add(sample.checkedAt);
        const bucket = buckets[Math.min(47, Math.floor((sample.checkedAt - start) / (DAY_MS / 48)))];
        bucket[sample.status]++;
        total++;
        if (sample.status === 'healthy')
            healthy++;
    }
    return { buckets, total, healthy, percent: total ? healthy / total * 100 : null };
}
//# sourceMappingURL=desktopMcp.js.map